/**
 * 웃긴 표정 러너 — 퀘스트 스팟 태블릿 미션(funny_photo).
 * 설계: docs/2026-06-19-quest-spot-mission-types-design.md §5.1·§5.2·§9.2 (D4 무저장)
 *
 * 동작:
 *   - 직원이 고른 후면/USB 카메라 미리보기. 실제 프레임 준비 후 카운트다운.
 *   - mission_config.timeout_sec 가 양의정수면 해당 초로, 없으면 기본 5초 카운트다운 후 자동 캡처.
 *   - 캡처 = 현재 프레임 1장을 canvas 에 그려 잠깐 표시 → 즉시 폐기.
 *     ❌ 업로드/서버전송/저장/base64 추출 없음(D4 무저장 — 아동 얼굴 클라우드 저장 원천 차단).
 *   - 캡처 사진 2초 → CRM 클리어 영상 종료 후 onComplete({captured:true}) 호출.
 *   - 좌절방지: 타임아웃이어도 통과(자동 캡처). 카메라 프레임이 비어도 captured:true.
 *
 * 부모(QuestTagPage)는 CAMERA_BLOCKED(http) 환경에서 본 컴포넌트를 렌더하지 않는다(안내만).
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import MissionIntroVideoScreen from './MissionIntroVideoScreen';
import { resolveFunnyPhotoClearVideoUrl } from './funnyPhotoMedia';
import './FunnyPhotoRunner.css';
import usePhotoCamera from './usePhotoCamera';
import { photoCameraError } from './photoCamera';

const FLASH_MS = 700; // "찰칵" 초록 틴트 연출 시간 — 짧게 번쩍 후 걷어 사진이 보이게
export const PREVIEW_MS = 2000; // 촬영 결과 사진 노출
const DEFAULT_TIMEOUT_SEC = 5;

/**
 * mission_config 에서 카운트다운 초를 확정한다.
 * 안내 화면(QuestTagPage 스캔 대기)이 "N초" 를 표기할 때 실제 러너 동작과 어긋나지 않도록
 * 계산 규칙을 이 파일 하나로 고정한다(값·조건 이중 정의 금지).
 * @param {{timeout_sec?:number}|null|undefined} config
 * @returns {number} 양의 정수 초
 */
export function resolveTimeoutSec(config) {
  return Number.isInteger(config?.timeout_sec) && config.timeout_sec > 0
    ? config.timeout_sec
    : DEFAULT_TIMEOUT_SEC;
}

/**
 * @param {object} props
 * @param {{timeout_sec?:number}|null} props.config - mission_config (timeout_sec 선택)
 * @param {(missionResult:{captured:true})=>void} props.onComplete
 * @param {()=>void} [props.onCancel]
 * @param {(err:Error)=>void} [props.onCameraError] - 카메라 시작 실패 시 부모 통지(앱 안 깨짐)
 * ⚠️ `spotName` prop 은 2026-08-07 에 제거했다 — 유일한 사용처였던 상단 타이틀 알약이
 *    3272:46598 에 없다. 사용자 확정: "카메라가 켜졌을 때는 어떤 미션인지 표시 없어도된다.
 *    이전 QR대기화면에서 이미 설명하고 있기 때문에".
 */
export default function FunnyPhotoRunner(props) {
  const [attempt, setAttempt] = useState(0);
  return <FunnyPhotoCapture key={attempt} {...props} onRetry={() => setAttempt(value => value + 1)} />;
}

function FunnyPhotoCapture({
  config, onComplete, onCancel, onCameraError, onVideoAbort, camera, onRetry,
}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const tickRef = useRef(null);
  const capturedRef = useRef(false); // 중복 캡처 방지
  const completedRef = useRef(false); // 중복 onComplete 방지

  const timeoutSec = resolveTimeoutSec(config);
  const clearVideoUrl = resolveFunnyPhotoClearVideoUrl(config);

  const [remaining, setRemaining] = useState(timeoutSec);
  const { ready: videoPlaying, error: cameraError, stop: stopCamera } = usePhotoCamera(videoRef, camera, 0, onCameraError);
  const [captured, setCaptured] = useState(false); // 캡처 프레임 미리보기 중
  const [showFlash, setShowFlash] = useState(false); // "찰칵" 초록 틴트 연출(캡처 직후 잠깐)
  // 🔴 스트림이 실제로 재생되기 전까지 영상을 가린다 (2026-08-19).
  //    `getUserMedia` 는 비동기라 렌더 직후~부착 사이에 **빈 `<video>`** 가 남고,
  //    Android WebView 가 그 구간에 네이티브 재생 오버레이를 그린다(사용자 보고).
  //    `onPlaying` 까지만 가리므로 카메라가 켜지는 순간 그대로 보인다.
  const [clearVideoShown, setClearVideoShown] = useState(false);

  const finish = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    onComplete({ captured: true });
  }, [onComplete]);

  // 연출 타이머 모음 — 언마운트 시 전부 해제해야 한다.
  // (해제하지 않으면 화면을 벗어난 뒤에도 onComplete 가 늦게 터져 엉뚱한 시점에 tag 가 나간다)
  const timersRef = useRef([]);
  const schedule = useCallback((fn, ms) => {
    const id = setTimeout(fn, ms);
    timersRef.current.push(id);
    return id;
  }, []);

  // ── 캡처: 현재 프레임 1장 → canvas 표시 후 폐기. 저장/전송 없음. ──
  const capture = useCallback(() => {
    if (capturedRef.current) return;
    capturedRef.current = true;

    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }

    // 프레임을 canvas 로 그려 잠깐 보여준다(연출). canvas 픽셀은 추출/전송하지 않는다.
    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (video && canvas && video.videoWidth > 0) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      }
    } catch {
      /* 프레임 캡처 실패해도 좌절방지 — 통과시킨다 */
    }
    // 캔버스에 프레임을 옮긴 뒤 카메라 하드웨어는 즉시 해제한다.
    stopCamera();
    setCaptured(true);
    setShowFlash(true);
    // "찰칵" 초록 틴트는 짧게만 — 이후 걷어 캡처한 사진이 그대로 보이게 한다.
    schedule(() => setShowFlash(false), FLASH_MS);

    // 마무리 연출 순서:
    //   ① 사진 미리보기 PREVIEW_MS(2초) → ② CRM 클리어 영상 → ③ onComplete
    // 클리어 영상이 없는 레거시 데이터는 2초 뒤 바로 완료 처리한다.
    // 프레임은 별도 저장/전송 없이 표시만 하고 폐기(D4 무저장).
    schedule(() => {
      if (clearVideoUrl) setClearVideoShown(true);
      else finish();
    }, PREVIEW_MS);
  }, [clearVideoUrl, finish, schedule, stopCamera]);

  // ── 카메라 시작 + 카운트다운 ──
  useEffect(() => {
    if (!videoPlaying || cameraError || capturedRef.current) return undefined;
    let seconds = timeoutSec;
    setRemaining(seconds);
    tickRef.current = setInterval(() => {
      seconds -= 1;
      setRemaining(Math.max(0, seconds));
      if (seconds <= 0) capture();
    }, 1000);
    return () => {
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
    };
  }, [timeoutSec, capture, videoPlaying, cameraError]);

  // 연출 타이머 해제는 **언마운트 전용 effect**로 분리한다.
  // 위 카메라 effect 에 두면 의존성(capture→onComplete)이 바뀌어 재실행될 때 타이머까지 취소되어
  // 미리보기→마무리→onComplete 시퀀스가 중간에 끊길 수 있다(화면이 멈춰버림).
  useEffect(
    () => () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    },
    []
  );

  if (cameraError) {
    return (
      <div className="fp-runner fp-runner-error" data-testid="funny-photo-camera-error">
        <span className="fp-error-icon">📷</span>
        <p className="fp-error-text">
          카메라를 열 수 없어요.
          <br />
          {photoCameraError(cameraError)}
        </p>
        <button className="fp-btn fp-btn-cancel" onClick={onRetry}>다시 시도</button>
        {onCancel && (
          <button className="fp-btn fp-btn-cancel" onClick={onCancel}>
            돌아가기
          </button>
        )}
      </div>
    );
  }

  // 사진을 2초 보여준 뒤 CRM 클리어 영상을 끝까지 재생한다.
  // 영상 종료 콜백에서만 태깅을 완료해 곧바로 QR 대기 화면으로 복귀한다.
  if (clearVideoShown) {
    return (
      <MissionIntroVideoScreen
        ariaLabel="사진찍기 미션 클리어 영상"
        logLabel="사진찍기 클리어 영상"
        videoUrl={clearVideoUrl}
        onFinished={finish}
        onAbort={(reason) => onVideoAbort?.(reason, clearVideoUrl)}
      />
    );
  }

  // 화면 구성은 Figma 3272:46598 (나무 액자 + 우상단 카운트다운 말풍선 + 좌하단 프레리독).
  // 레이어 순서: 카메라(화면 전체) → 액자(가운데 투명) → 프레리독 → 말풍선.
  //   액자 가운데가 투명하므로 카메라를 통째로 깔고 위에서 덮으면 창이 만들어진다
  //   (옛 2300:106265 처럼 배경 속 빈 사각형 좌표를 픽셀 검출할 필요가 없다).
  return (
    <div
      className={`fp-shoot${captured ? ' fp-shoot-done' : ''}`}
      data-testid="funny-photo-runner"
    >
      {/* 카메라 뷰 → 캡처 후에는 화면 정중앙의 사진 상자로 전환(같은 노드 유지) */}
      <div className="fp-shoot-stage">
        <video
          ref={videoRef}
          className={`fp-shoot-video${videoPlaying ? ' fp-shoot-video-playing' : ''}`}
          autoPlay
          playsInline
          muted
          data-testid="funny-photo-video"
        />
        {/* 캡처 프레임 미리보기(연출) — 픽셀은 추출/전송하지 않음 */}
        <canvas
          ref={canvasRef}
          className={`fp-shoot-canvas ${captured ? 'fp-shoot-canvas-show' : ''}`}
          data-testid="funny-photo-canvas"
        />
        {/* "찰칵" 연출은 초록 틴트 번쩍임만 — 문구는 넣지 않는다 */}
        {captured && showFlash && (
          <div className="fp-shoot-flash" data-testid="funny-photo-flash" />
        )}
      </div>

      {/* 촬영 중에만 보이는 액자 요소 — 캡처 후에는 파스텔 배경 + 사진만 남긴다 */}
      {!captured && (
        <>
          {/* 🔴 나무 액자 — **가운데가 투명한 이미지**를 카메라 위에 덮는다 (3304:44134).
              옛 방식은 불투명 배경을 깔고 그 안의 회색 사각형 bbox 를 픽셀 검출해 카메라를
              얹었는데(각 변 3px 여유까지 필요했다), 투명 액자를 덮으면 그 좌표가 통째로
              필요 없어진다. 터치는 통과시켜야 하므로 CSS 에서 pointer-events: none. */}
          <span className="fp-shoot-frame" aria-hidden="true" />

          {/* 좌하단 프레리독 사진사 (3304:44136) */}
          <span className="fp-shoot-photographer" aria-hidden="true" />

          {/* 우상단 말풍선 — 카운트다운(5→4→3→2→1, 1초 간격) */}
          <div className="fp-shoot-bubble fp-shoot-bubble-right">
            <span className="fp-shoot-bubble-img" aria-hidden="true" />
            <span
              className="fp-shoot-count"
              data-testid="funny-photo-countdown"
              /* 숫자 그림은 CSS 스프라이트가 고른다 — 이 속성이 없으면 빈 칸이 뜬다. */
              data-count={remaining}
              aria-live="polite"
              key={remaining} /* 값이 바뀔 때마다 새 노드 → 톡 튀는 전환 애니메이션 재생 */
            >
              {remaining}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
